import { EventEmitter } from '@angular/core';
import * as firebase from 'firebase';

export class Uploader {
  private _fileTypes: RegExp = Uploader.fileTypes.all;
  private _status: Uploader.UploadStatus;
  private _processImages = false;

  files: Uploader.FileItem[] = [];
  allowDuplicates = true;
  acceptTypes: string;
  maxFiles = 0;
  maxSize = 0;
  exactRes = '';
  maxRes = '';
  minRes = '';

  storagePath = '';

  statusChanged: EventEmitter<Uploader.UploadStatus> = new EventEmitter;
  fileAdded: EventEmitter<Uploader.FileItem> = new EventEmitter;
  fileUploaded: EventEmitter<Uploader.FileItem> = new EventEmitter;
  fileDeleted: EventEmitter<Uploader.FileItem> = new EventEmitter;

  get status(): Uploader.UploadStatus {
    return this._status;
  }
  set status(value: Uploader.UploadStatus) {
    this._status = value;
    this.statusChanged.emit(value);
  }

  get fileTypes() {
    return this._fileTypes;
  }
  set fileTypes(value: Uploader.fileType) {
    if (value instanceof RegExp) {
      this._fileTypes = value;
      this.acceptTypes = '*';
    } else {
      this._processImages = (value === 'images' || value === 'webImages');
      this._fileTypes = Uploader.fileTypes[value];
      this.acceptTypes = Uploader.acceptTypes[value];
    }
  }

  _preventAndStop(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }

  _dupFile(file: File): number {
    return this.files.findIndex(f => f.file.name === file.name);
  }

  _validFileType(file: File | DataTransferItem): boolean {
    return this._fileTypes.test(file.type);
  }

  _validFileRes(file: File): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const image = new Uploader.UploadImage(file);
      image.onload = () => {
        if (!(image.height + image.width)) {
          reject(Uploader.UploadStatus.INVALID_IMAGE);
        } else {
          const maxRes = this.maxRes.split(/[^\d.]/).map(Number.parseFloat).filter(Number.isFinite);
          const minRes = this.minRes.split(/[^\d.]/).map(Number.parseFloat).filter(Number.isFinite);
          const exactRes = this.exactRes.split(/[^\d.]/).map(Number.parseFloat).filter(Number.isFinite);

          const valid = (
            (
              !(exactRes.length > 1) ||
              (image.width === exactRes[0]) && (image.height === exactRes[1])
            )
              &&
            (
              !(minRes.length > 1) ||
                ((image.width >= minRes[0]) && (image.height >= minRes[1]))
            )
              &&
            (
              !(maxRes.length > 1) ||
                ((image.width <= maxRes[0]) && (image.height <= maxRes[1]))
            )
          );

          if (valid) {
            resolve(valid);
          } else {
            reject(Uploader.UploadStatus.INVALID_RES);
          }
        }
      };
    });
  }

  _validFileSize(file: File): boolean {
    return !this.maxSize || file.size < this.maxSize;
  }

  _fileListToFileItems(files: FileList) {
    return Array.from(files).map(file => new Uploader.FileItem(file));
  }

  addFiles(files: Uploader.FileItem[]): void {
    let dupIndex;

    if (this.files.length + files.length > this.maxFiles) {
      this.status = Uploader.UploadStatus.MAX_FILES;
    } else {
      Array.from(files).forEach(file => {
        if (!this._validFileType(file.file)) {
          this.status = Uploader.UploadStatus.INVALID_TYPE;
        } else if (!this._validFileSize(file.file)) {
          this.status = Uploader.UploadStatus.MAX_SIZE;
        } else if (!this.allowDuplicates && (dupIndex = this._dupFile(file.file)) > -1) {
          this.status = Uploader.UploadStatus.DUPLICATED_NAME;
        } else {
          this.status = null;
          if (this._processImages) {
            this._validFileRes(file.file).then(() => {
              file.url = URL.createObjectURL(file.file);
              this.files.push(file);
              this.fileAdded.emit(file);
            }).catch(status =>
              this.status = status
            );
          } else {
            file.url = URL.createObjectURL(file.file);
            this.files.push(file);
            this.fileAdded.emit(file);
          }
        }
      });
    }
  }

  removeFile(index: number) {
    const file = this.files[index];
    file.cancel();
    this.deleteFile(file);
    this.files.splice(index, 1);
  }

  deleteFile(file: Uploader.FileItem, path?: string) {
    const ref = firebase.storage().ref();
    ref.child(`${path || this.storagePath}/${file.name}`).delete();
    this.files.splice(this.files.indexOf(file), 1);
    this.fileDeleted.next(file);
  }

  clearFiles() {
    this.files.splice(0);
  }

  uploadBlob(blob: Blob, filename: string, type: string, path?: string, extra?: any) {
    this.uploadFile(
      new Uploader.FileItem(
        new File([blob], filename, {type: type})
      ),
      path, extra
    );
  }

  uploadFile(file: Uploader.FileItem, path?: string, extra?: any) {
    if (file.status === Uploader.FileItemStatus.CANCELLED) {
      return; // Abort cancelled files
    }

    const ref = firebase.storage().ref();
    const task: firebase.storage.UploadTask =
      ref.child(`${path || this.storagePath}/${file.name}`)
        .put(file.file, {contentType: file.file.type});

    Object.assign(file, extra);

    file.task = task;
    file.status = Uploader.FileItemStatus.UPLOADING;

    task.on(firebase.storage.TaskEvent.STATE_CHANGED,
      {
        next: () => {
          file.progress = (task.snapshot.bytesTransferred / task.snapshot.totalBytes) * 100;
        },
        error: (error) => {
          file.status = Uploader.FileItemStatus.ERROR;
          this.status = Uploader.UploadStatus.UPLOAD_ERROR;
        },
        complete: async () => {
          file.status = Uploader.FileItemStatus.UPLOADED;
          file.url = await task.snapshot.ref.getDownloadURL();
          this.fileUploaded.emit(file);
        }
      }
    );
  }
}

export module Uploader {
  export class FileItem {
    status: Uploader.FileItemStatus = Uploader.FileItemStatus.WAITING;
    progress = 0;
    name = '';
    url = '';
    task: firebase.storage.UploadTask;

    constructor(
      public file: File
    ) {
      this.name = file.name;
    }

    cancel() {
      if (this.task) {
        this.task.cancel();
      }
      this.status = Uploader.FileItemStatus.CANCELLED;
    }
  }

  export class UploadImage {
    private _file: File;
    private _img: HTMLImageElement;

    get file(): File {
      return this._file;
    }

    set file(value: File) {
      this._file = value;
      this._img = new Image;
      this._img.src = URL.createObjectURL(value);
    }

    get image(): HTMLImageElement {
      return this._img;
    }

    get height() { return this._img.height; }
    get width() { return this._img.width; }
    get naturalHeight() { return this._img.naturalHeight; }
    get naturalWidth() { return this._img.naturalWidth; }
    get sizes() { return this._img.sizes; }

    get onload() { return this._img.onload; }
    set onload(event) { this._img.onload = event; }

    constructor(file?: File) {
      this.file = file;
    }
  }

  export enum FileItemStatus {
    UPLOADING = 'uploading',
    CANCELLED = 'cancelled',
    UPLOADED = 'uploaded',
    WAITING = 'waiting',
    ERROR = 'error',
  }

  export enum UploadStatus {
    VALID = 'valid',

    DUPLICATED_NAME = 'duplicatedName',

    INVALID_DROP = 'invalidDrop',
    INVALID_TYPE = 'invalidType',
    INVALID_IMAGE = 'invalidImage',
    INVALID_RES = 'invalidRes',

    MAX_FILES = 'maxFiles',
    MAX_SIZE = 'maxSize',

    UPLOAD_CANCELLED = 'uploadCancelled',
    UPLOAD_ERROR = 'uploadError',
  }

  export const fileTypes = {
    webImages: /^image\/(png|jpe?g)/i,
    images: /^image\/.*/i,
    all: /^\w+\/[\w.+-]+$/,
  };

  export const acceptTypes = {
    webImages: '.png,.jpg,.jpeg',
    images: 'image/*',
    all: '*',
  };

  export type fileType = ('all' | 'images' | 'webImages' | RegExp);

}
